import React, { useCallback, useState } from 'react';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { ApplicationState } from 'app/types-store';
import { OverlayComponent } from 'app/components';
import * as navigate from 'app/area/shared/navigation';

import { SwitchDisplayDisabled } from 'app/area/shared/saved-reports/components/switch-display-disabled';
import { createDefaultSavedReport } from 'app/area/shared/saved-reports/utils/create-default-saved-report';
import { SavedReport, SavedReportValidation } from 'app/area/shared/saved-reports/types-store';
import { LoadEvaluationHandler } from 'app/area/shared/saved-reports/types';
import { getBackRoute } from '../../common/route';
import { DashboardAdminTranslation } from '../../constants';
import { DashboardSavedReportsState } from '../types-store';
import { SavedReportsHeader } from './saved-reports-header';
import { SavedReportsList } from './saved-reports-list';
import { SavedReportsModal } from './saved-reports-modal';

import styles from './saved-reports-renderer.scss';

interface SavedReportsRendererProps {
  savedReportsState: DashboardSavedReportsState;
  dashboardTypeId: number;
  searchFavouriteId: number;

  saveSavedReports: () => void;

  selectCurrentSavedReports: (savedReport: SavedReport, onlyIdMode: boolean) => void;
  updateCurrentSavedReports: (savedReport: Partial<SavedReport>) => void;
  updateCurrentValidation: (validation: SavedReportValidation) => void;

  loadDashboardEvaluation: LoadEvaluationHandler;

  updateSavedReport: (currentSavedReportId: number, savedReport: SavedReport, validation: SavedReportValidation) => void;
  deleteSavedReport: (savedReportId: number) => void;

  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    }
  };
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;

  displayError: (errorMessage: string) => void;

  switchDisplayDisabled: (displayDisabled: boolean) => void;

  locale: string;
  translation: DashboardAdminTranslation;
}
export const SavedReportsRenderer: React.FC<SavedReportsRendererProps> = ({
  dashboardTypeId, searchFavouriteId, savedReportsState,
  selectCurrentSavedReports, updateCurrentSavedReports, updateCurrentValidation,
  loadDashboardEvaluation, updateSavedReport, deleteSavedReport, saveSavedReports,
  options, loadOptions,
  displayError,
  switchDisplayDisabled,
  locale, translation
}) => {

  const { description, saving, displayDisabled,
    currentSavedReportId, currentSavedReport, currentSavedReportOnlyIdMode, currentValidation,
    savedReports, savedReportsValidation } = savedReportsState;

  const onCancel = useCallback(() => {
    navigate.to({ pathname: getBackRoute(dashboardTypeId, searchFavouriteId) });
  }, []);

  const [showModal, switchShowModal] = useState(false);

  const addSavedReport = useCallback(() => {
    selectCurrentSavedReports(createDefaultSavedReport(undefined, savedReports.length + 1), false);
    switchShowModal(true);
  }, [savedReports.length]);

  const editSavedReport = useCallback((savedReport: SavedReport) => {
    selectCurrentSavedReports(savedReport, false);
    switchShowModal(true);
  }, [savedReports]);

  const closeModal = useCallback(() => {
    selectCurrentSavedReports(undefined, false);
    switchShowModal(false);
  }, []);

  const loadEvaluation = useCallback(
    (favouriteId: number, onUpdateSavedReport: (savedReport: SavedReport) => void) => {
      const getState = (state: ApplicationState) => state?.dashboardAdministration?.savedReports;
      const onSavedReportError = () => {
        displayError(translation.fields_are_filled_out_incorrectly);
      };
      loadDashboardEvaluation(dashboardTypeId, favouriteId, onUpdateSavedReport, onSavedReportError, getState);
    }, [dashboardTypeId]
  );

  return <>
    <OverlayComponent
      displayOverlay={saving}
      overlayText={translation.saving_dashboard} />
    {showModal && !!currentSavedReport && <SavedReportsModal
      currentSavedReportId={currentSavedReportId}
      currentSavedReport={currentSavedReport}
      currentSavedReportOnlyIdMode={currentSavedReportOnlyIdMode}
      allSavedReportIs={savedReports?.map(report => report.code?.value)}
      updateCurrent={updateCurrentSavedReports}
      updateSavedReport={updateSavedReport}
      closeModal={closeModal}
      loadEvaluation={loadEvaluation}
      options={options}
      loadOptions={loadOptions}
      updateValidation={updateCurrentValidation}
      displayError={displayError}
      locale={locale}
      validation={currentValidation}
      translation={translation} />}
    <SavedReportsHeader
      description={description}
      translation={translation}
      onSave={saveSavedReports}
      onCancel={onCancel}
      onAddNew={addSavedReport}
    />
    <MainGridContainer className={`dashboard ${styles.dashboardView}`} wide={true}>
      <SwitchDisplayDisabled
        label={translation.show_disabled_elements}
        active={displayDisabled}
        onChange={switchDisplayDisabled} />
      <SavedReportsList
        savedReportsState={savedReportsState}
        editSavedReport={editSavedReport}
        updateSavedReport={updateSavedReport}
        deleteSavedReport={deleteSavedReport}
        onAddNew={addSavedReport}
        validation={savedReportsValidation}
        translation={translation} />
    </MainGridContainer>
  </>;
};
